import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, FileText, Users, Briefcase, Settings, Plus,
  LayoutDashboard, FolderOpen, BarChart2, HelpCircle,
  Command, ArrowRight, Clock, Star, Zap, Sparkles,
  MessageSquare, Wand2, FileSearch, BookOpen, Lightbulb
} from 'lucide-react';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ElementType;
  action: () => void;
  category: 'navigation' | 'action' | 'recent' | 'ai';
  keywords?: string[];
  iconColor?: string;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  // AI feature callbacks
  onOpenAIAssistant?: () => void;
  onOpenTemplateGenerator?: () => void;
  onOpenDocumentAnalyzer?: () => void;
  onOpenSmartFill?: () => void;
  onOpenClauseSuggestions?: () => void;
}

export function CommandPalette({ 
  isOpen, 
  onClose,
  onOpenAIAssistant,
  onOpenTemplateGenerator,
  onOpenDocumentAnalyzer,
  onOpenSmartFill,
  onOpenClauseSuggestions,
}: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // AI Commands
  const aiCommands: CommandItem[] = [
    { 
      id: 'ai-assistant', 
      label: 'AI Assistant', 
      description: 'Chat with Claude for help', 
      icon: MessageSquare, 
      action: () => onOpenAIAssistant?.(), 
      category: 'ai', 
      keywords: ['chat', 'help', 'claude', 'ask'],
      iconColor: 'from-amber-500 to-orange-600'
    },
    { 
      id: 'ai-generate', 
      label: 'Generate Template with AI', 
      description: 'Create a template from description', 
      icon: Wand2, 
      action: () => onOpenTemplateGenerator?.(), 
      category: 'ai', 
      keywords: ['create', 'new', 'ai', 'generate', 'magic'],
      iconColor: 'from-violet-500 to-purple-600'
    },
    { 
      id: 'ai-analyze', 
      label: 'Analyze Document', 
      description: 'Review for risks and key terms', 
      icon: FileSearch, 
      action: () => onOpenDocumentAnalyzer?.(), 
      category: 'ai', 
      keywords: ['review', 'check', 'risks', 'analyze'],
      iconColor: 'from-cyan-500 to-blue-600'
    },
    { 
      id: 'ai-smartfill', 
      label: 'Smart Fill Variables', 
      description: 'Extract data from plain text', 
      icon: BookOpen, 
      action: () => onOpenSmartFill?.(), 
      category: 'ai', 
      keywords: ['extract', 'fill', 'variables', 'auto'],
      iconColor: 'from-emerald-500 to-teal-600'
    },
    { 
      id: 'ai-clauses', 
      label: 'Suggest Clauses', 
      description: 'Get AI clause recommendations', 
      icon: Lightbulb, 
      action: () => onOpenClauseSuggestions?.(), 
      category: 'ai', 
      keywords: ['suggest', 'recommend', 'clauses', 'add'],
      iconColor: 'from-pink-500 to-rose-600'
    },
  ];

  const commands: CommandItem[] = [
    // Navigation
    { id: 'nav-dashboard', label: 'Go to Dashboard', icon: LayoutDashboard, action: () => navigate('/dashboard'), category: 'navigation', keywords: ['home', 'overview'] },
    { id: 'nav-templates', label: 'Go to Templates', icon: FileText, action: () => navigate('/templates'), category: 'navigation', keywords: ['documents', 'dsl'] },
    { id: 'nav-clauses', label: 'Go to Clause Library', icon: FolderOpen, action: () => navigate('/clauses'), category: 'navigation', keywords: ['snippets', 'reusable'] },
    { id: 'nav-clients', label: 'Go to Clients', icon: Users, action: () => navigate('/clients'), category: 'navigation', keywords: ['contacts', 'people'] },
    { id: 'nav-matters', label: 'Go to Matters', icon: Briefcase, action: () => navigate('/matters'), category: 'navigation', keywords: ['cases', 'projects'] },
    { id: 'nav-documents', label: 'Go to Documents', icon: FileText, action: () => navigate('/documents'), category: 'navigation', keywords: ['files', 'generated'] },
    { id: 'nav-reports', label: 'Go to Reports', icon: BarChart2, action: () => navigate('/reports'), category: 'navigation', keywords: ['analytics', 'stats'] },
    { id: 'nav-settings', label: 'Go to Settings', icon: Settings, action: () => navigate('/settings'), category: 'navigation', keywords: ['preferences', 'config'] },
    { id: 'nav-help', label: 'Go to Help & Docs', icon: HelpCircle, action: () => navigate('/help'), category: 'navigation', keywords: ['documentation', 'faq'] },
    
    // Actions
    { id: 'action-new-template', label: 'Create New Template', description: 'Start a new document template', icon: Plus, action: () => navigate('/templates/new'), category: 'action', keywords: ['add', 'create'] },
    { id: 'action-new-client', label: 'Add New Client', description: 'Create a new client record', icon: Plus, action: () => navigate('/clients?action=new'), category: 'action', keywords: ['add', 'create'] },
    { id: 'action-new-matter', label: 'Create New Matter', description: 'Start a new matter', icon: Plus, action: () => navigate('/matters?action=new'), category: 'action', keywords: ['add', 'create', 'case'] },
    { id: 'action-generate', label: 'Generate Document', description: 'Create a document from a template', icon: Zap, action: () => navigate('/templates?action=generate'), category: 'action', keywords: ['create', 'pdf'] },
  ];

  const recentItems: CommandItem[] = [
    { id: 'recent-1', label: 'Wisconsin Warranty Deed', description: 'Edited 2 hours ago', icon: Clock, action: () => navigate('/templates/1'), category: 'recent' },
    { id: 'recent-2', label: 'Smith Holdings LLC', description: 'Client • 3 matters', icon: Clock, action: () => navigate('/clients/1'), category: 'recent' },
    { id: 'recent-3', label: 'Property Purchase - 123 Main St', description: 'Matter • Active', icon: Clock, action: () => navigate('/matters/1'), category: 'recent' },
  ];

  const allCommands = [...recentItems, ...aiCommands, ...commands];

  const filteredCommands = query
    ? allCommands.filter(cmd => {
        const searchStr = `${cmd.label} ${cmd.description || ''} ${cmd.keywords?.join(' ') || ''}`.toLowerCase();
        return searchStr.includes(query.toLowerCase());
      })
    : allCommands;

  const groupedCommands = {
    recent: filteredCommands.filter(c => c.category === 'recent'),
    ai: filteredCommands.filter(c => c.category === 'ai'),
    action: filteredCommands.filter(c => c.category === 'action'),
    navigation: filteredCommands.filter(c => c.category === 'navigation'),
  };

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const executeCommand = useCallback((command: CommandItem) => {
    command.action();
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, filteredCommands.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          executeCommand(filteredCommands[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  }, [filteredCommands, selectedIndex, executeCommand, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    const selectedEl = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    selectedEl?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!isOpen) return null;

  let flatIndex = 0;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Command palette */}
        <div className="absolute inset-0 flex items-start justify-center pt-[15vh]">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            className="w-full max-w-xl bg-slate-900 rounded-xl shadow-2xl border border-slate-700 overflow-hidden"
          >
            {/* Search input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-700">
              <Search className="w-5 h-5 text-slate-500" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search commands, AI features, or pages..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 bg-transparent text-white placeholder-slate-500 outline-none text-base"
              />
              <kbd className="px-2 py-1 bg-slate-800 rounded text-xs text-slate-400 font-mono">ESC</kbd>
            </div>

            {/* Results */}
            <div ref={listRef} className="max-h-96 overflow-y-auto p-2">
              {filteredCommands.length === 0 ? (
                <div className="py-8 text-center text-slate-500">
                  <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No results found for "{query}"</p>
                </div>
              ) : (
                <>
                  {/* Recent */}
                  {groupedCommands.recent.length > 0 && (
                    <div className="mb-2">
                      <p className="px-3 py-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Recent
                      </p>
                      {groupedCommands.recent.map((cmd) => {
                        const index = flatIndex++;
                        return (
                          <CommandRow
                            key={cmd.id}
                            command={cmd}
                            isSelected={selectedIndex === index}
                            onClick={() => executeCommand(cmd)}
                            dataIndex={index}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* AI Features */}
                  {groupedCommands.ai.length > 0 && (
                    <div className="mb-2">
                      <p className="px-3 py-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2">
                        <Sparkles className="w-3 h-3 text-amber-400" />
                        AI Features
                      </p>
                      {groupedCommands.ai.map((cmd) => {
                        const index = flatIndex++;
                        return (
                          <CommandRow
                            key={cmd.id}
                            command={cmd}
                            isSelected={selectedIndex === index}
                            onClick={() => executeCommand(cmd)}
                            dataIndex={index}
                            isAI
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* Actions */}
                  {groupedCommands.action.length > 0 && (
                    <div className="mb-2">
                      <p className="px-3 py-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Actions
                      </p>
                      {groupedCommands.action.map((cmd) => {
                        const index = flatIndex++;
                        return (
                          <CommandRow
                            key={cmd.id}
                            command={cmd}
                            isSelected={selectedIndex === index}
                            onClick={() => executeCommand(cmd)}
                            dataIndex={index}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* Navigation */}
                  {groupedCommands.navigation.length > 0 && (
                    <div>
                      <p className="px-3 py-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Navigation
                      </p>
                      {groupedCommands.navigation.map((cmd) => {
                        const index = flatIndex++;
                        return (
                          <CommandRow
                            key={cmd.id}
                            command={cmd}
                            isSelected={selectedIndex === index}
                            onClick={() => executeCommand(cmd)}
                            dataIndex={index}
                          />
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-slate-700 flex items-center justify-between text-xs text-slate-500">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-slate-800 rounded font-mono">↑</kbd>
                  <kbd className="px-1.5 py-0.5 bg-slate-800 rounded font-mono">↓</kbd>
                  navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-slate-800 rounded font-mono">↵</kbd>
                  select
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Command className="w-3 h-3" />
                <span>K to open anytime</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
}

interface CommandRowProps {
  command: CommandItem;
  isSelected: boolean;
  onClick: () => void;
  dataIndex: number;
  isAI?: boolean;
}

function CommandRow({ command, isSelected, onClick, dataIndex, isAI }: CommandRowProps) {
  const Icon = command.icon;
  
  return (
    <button
      data-index={dataIndex}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
        isSelected
          ? 'bg-accent-500/20 text-accent-400'
          : 'text-slate-300 hover:bg-slate-800'
      }`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
        isAI && command.iconColor
          ? `bg-gradient-to-br ${command.iconColor}`
          : isSelected 
            ? 'bg-accent-500/30' 
            : 'bg-slate-800'
      }`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 text-left">
        <p className="font-medium">{command.label}</p>
        {command.description && (
          <p className={`text-xs ${isSelected ? 'text-accent-400/70' : 'text-slate-500'}`}>
            {command.description}
          </p>
        )}
      </div>
      {isSelected && (
        <ArrowRight className="w-4 h-4 text-accent-400" />
      )}
    </button>
  );
}

// Hook to manage command palette state
export function useCommandPalette() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen(prev => !prev),
  };
}
